import { ILoadPayloadDTO } from '../dtos/load-payload-dto';
import { ContractManagementAPIHelper } from '../helpers/contract-management-api.helper';
import { ContractManagementMutations } from '@/api/contract-management/graphql/contract-management.mutations';
import { ContractManagementQueries } from '@/api/contract-management/graphql/contract-management.queries';
import { EFetchPolicy } from '@/api/types/enums/fetch-policy';
import { apolloClient } from '@/apollo';
import { IContractManagementFilterRanges } from '@/contract-management/types/contract-management-filter-ranges';
import { IContractManagementKeyValues } from '@/contract-management/types/contract-management-key-values';
import { EnumDocumentNotificationTypes } from '@/enums/EnumDocumentNotificationTypes';
import { IFolder } from '@/folders/types/folder';
import { ICreateNotificationRequest } from '@/types/CreateNotificationRequest';
import { IDownloadDocument } from '@/contract-management/types/download-document';
import { IEditDocumentsPayload } from '@/contract-management/types/edit-documents-payload';
import { IUploadDocument } from '@/types/UploadDocument';
import { IDocument } from '@lib-shared/types/Document';
import { IDocumentNotification } from '@lib-shared/types/DocumentNotification';
import { ITag } from '@lib-shared/types/Tag';

export class ContractManagementService {
  static async loadDocuments(payload: ILoadPayloadDTO): Promise<IDocument[]> {
    const {
      data: { contractManagement },
    } = await apolloClient.query({
      query: ContractManagementQueries.contractManagementDocumentsQuery,
      variables: { ...payload },
      fetchPolicy: EFetchPolicy.NetworkOnly,
    });
    return contractManagement;
  }

  static async loadDownloadUrls(documentIds: string[]): Promise<IDownloadDocument[]> {
    const {
      data: { downloadDocuments },
    } = await apolloClient.query({
      query: ContractManagementQueries.fetchDownloadDocuments,
      variables: { documentIds },
    });
    return downloadDocuments;
  }

  static async loadKeyValues(payload: ILoadPayloadDTO): Promise<IContractManagementKeyValues> {
    const {
      data: { contractManagementKeyValues },
    } = await apolloClient.query({
      query: ContractManagementQueries.contractManagementKeyValuesQuery,
      variables: {
        ...payload,
      },
      fetchPolicy: EFetchPolicy.NetworkOnly,
    });
    return contractManagementKeyValues;
  }

  static async uploadDocument(payload: IUploadDocument): Promise<IDocument> {
    const { data } = await apolloClient.mutate({
      mutation: ContractManagementMutations.archiveDocumentMutation,
      variables: payload,
    });
    return data.archiveDocument;
  }

  static async createFolder(name: string, parentFolderId: string): Promise<IFolder> {
    const { data } = await apolloClient.mutate({
      mutation: ContractManagementMutations.createFolderMutation,
      variables: { name, parentFolderId },
    });
    return data.createFolder;
  }

  static async updateFolderName(folderId: string, name: string): Promise<IFolder> {
    const {
      data: { updateFolder },
    } = await apolloClient.mutate({
      mutation: ContractManagementMutations.updateFolderMutation,
      variables: { folderId, name },
    });
    return updateFolder;
  }

  static async deleteFolder(id: string): Promise<IFolder> {
    const { data } = await apolloClient.mutate({
      mutation: ContractManagementMutations.deleteFolderMutation,
      variables: { id },
    });
    return data.deleteFolder;
  }

  static async updateDocumentsFolder(documentIds: string[], folderId: string): Promise<void> {
    const {
      data: { bulkMutationResponse },
    } = await apolloClient.mutate({
      mutation: ContractManagementMutations.changeDocumentsFolderMutation,
      variables: { documentIds, folderId },
    });
    return bulkMutationResponse;
  }

  static async deleteDocuments(ids: string[]): Promise<IDocument[]> {
    const { data } = await apolloClient.mutate({
      mutation: ContractManagementMutations.deleteDocumentsMutation,
      variables: { ids },
    });
    return data.deleteDocuments;
  }

  static async editDocuments({
    payload,
    documentIds,
  }: {
    payload: IEditDocumentsPayload;
    documentIds: string[];
  }): Promise<IDocument[]> {
    const {
      data: { updateDocuments },
    } = await apolloClient.mutate({
      mutation: ContractManagementMutations.editDocumentsMutation,
      variables: { ...payload, documentIds },
    });
    return updateDocuments;
  }

  static async updateDocumentOwner(documentId: string, userId: string): Promise<IDocument> {
    const { data } = await apolloClient.mutate({
      mutation: ContractManagementMutations.updateDocumentOwnerMutation,
      variables: { documentId, userId },
    });
    return data.updateDocumentOwner;
  }

  static async addTagsToDocuments(documentIds: string[], tagIds: string[]): Promise<string[]> {
    const { data } = await apolloClient.mutate({
      mutation: ContractManagementMutations.addTagsMutation,
      variables: { documentIds, tagIds },
    });
    return data.addDocumentTags;
  }

  static async deleteDocumentTags(documentIds: string[], tagIds: string[]): Promise<boolean> {
    const {
      data: { removeDocumentTags },
    } = await apolloClient.mutate({
      mutation: ContractManagementMutations.removeTagsMutation,
      variables: { documentIds, tagIds },
    });
    return removeDocumentTags;
  }

  static async updateNotification(
    id: string,
    userId: string,
    documentId: string,
    notificationType: EnumDocumentNotificationTypes,
    notificationNote: string,
    notificationDate: Date
  ): Promise<IDocumentNotification> {
    const { data } = await apolloClient.mutate({
      mutation: ContractManagementMutations.updateNotificationMutation,
      variables: {
        id,
        userId,
        documentId,
        notificationType,
        notificationNote,
        notificationDate,
      },
    });
    return data.updateNotification;
  }

  static async deleteDocumentNotification(id: string): Promise<IDocumentNotification> {
    const { data } = await apolloClient.mutate({
      mutation: ContractManagementMutations.deleteNotificationMutation,
      variables: {
        id,
      },
    });
    return data.deleteDocumentNotification;
  }

  static async loadFilterRanges(): Promise<IContractManagementFilterRanges> {
    const {
      data: { contractManagementFilterRanges },
    } = await apolloClient.query({
      query: ContractManagementQueries.filterRangesQuery,
    });
    return ContractManagementAPIHelper.verifyFilterRanges(contractManagementFilterRanges);
  }

  static async loadAllTags(): Promise<ITag[]> {
    const {
      data: { entity },
    } = await apolloClient.query({
      query: ContractManagementQueries.allTagsQuery,
      fetchPolicy: EFetchPolicy.NoCache,
    });
    return entity.tags;
  }

  static async createTag(title: string): Promise<ITag> {
    const { data } = await apolloClient.mutate({
      mutation: ContractManagementMutations.createTagMutation,
      variables: { title },
    });
    return data.createTag;
  }

  static async loadDocumentNotifications(): Promise<IDocumentNotification[]> {
    const {
      data: { documentNotifications },
    } = await apolloClient.query({
      query: ContractManagementQueries.documentNotificationsQuery,
    });
    return documentNotifications;
  }

  static async createNotifications(
    notifications: ICreateNotificationRequest[]
  ): Promise<IDocumentNotification[]> {
    return Promise.all(
      notifications.map(async (notification: ICreateNotificationRequest) => {
        const { data } = await apolloClient.mutate({
          mutation: ContractManagementMutations.createDocumentNotification,
          variables: {
            documentIds: [notification.documentId],
            notificationDate: notification.notificationDate,
            notificationNote: notification.notificationNote,
            notificationType: notification.notificationType,
            userId: notification.userId,
          },
        });
        return data.createDocumentNotification[0];
      })
    );
  }

  static async deleteNotifications(ids: string[]): Promise<void> {
    await apolloClient.mutate({
      mutation: ContractManagementMutations.deleteDocumentNotificationMutation,
      variables: { ids },
    });
  }
}
